import { graphql } from 'react-apollo';
import log from 'chameleon/ui-stack/utilities/log';

import { getAggregateResults } from 'legacy-src/queries_aggregate';

/* This sets off a chain:
 * 1. we reach out to the graphql service to get information about the assignment/assessment, questions, students
 *    and sign a reports request so we can get the results for the students.
 *    - `getAggregateResults`
 * 2. We use the question and assessment info to update `questionDataset` and `studentDataset` in the state;
 * 3. throughout all this we've been updating the props on the "children" so they have what they need.
 * 4. `render`
 */
const withAggregateResults = graphql(getAggregateResults, {
  options: ({
    params: { subject, assignmentId },
    userData: { chosenEducationPeriodCode },
  }) => {
    const assignmentIdInt = parseInt(assignmentId, 10);
    const subjectIdInt = parseInt(subject, 10);
    let hierarchy;
    switch (subjectIdInt) {
      case 107:
        hierarchy = 'PreAP_WHG_Item_Type';
        break;
      case 106:
      case 112:
        hierarchy = 'PreAP_Literacy_Strand';
        break;
      default:
        hierarchy = 'PreAP_Area_of_Focus';
        break;
    }
    return {
      variables: {
        subjectId: subjectIdInt,
        assignmentId: assignmentIdInt,
        educationPeriod: chosenEducationPeriodCode,
        htmlId: 'aggregate-results',
        hierarchy,
      },
      fetchPolicy: 'network-only',
    };
  },
  props: ({ data, ownProps }) => {
    if (data.loading) {
      return;
    }
    if (data.error) {
      log('error', data.error);
      return {
        providerError: true,
      };
    }

    const {
      params,
      userData: { me, isStudent },
    } = ownProps;

    const studentId = isStudent ? me.initId : params.studentId;

    if (data.aggregateAssignmentInfo) {
      const {
        aggregateAssignmentInfo: {
          assignmentsResults: {
            lastScoreByItem: lastScoreByItemJsonString,
            lastScoreByTag: lastScoreByTagJsonString,
            assignmentMetadata,
            questions,
            scoringRubricResults: scoringRubricResultsJsonString,
            responseAnalysis: responseAnalysisJsonString,
            sessionTemplate: sessionTemplateJsonString,
            scoringSessionTemplate: scoringSessionTemplateJsonString,
          },
          scoringRubricRequest,
          responseAnalysisSignedRequest,
          sectionName,
          name: assessmentName,
        },
      } = data;
      let questionTagsById = {};
      // we might get multiple back, so merge them into one.
      assignmentMetadata.forEach((m) =>
        m.questionTagsById.forEach((q) => (questionTagsById[q.accNum] = q)),
      );

      // we get a json string of json...
      const lastScoreByItem = JSON.parse(lastScoreByItemJsonString);
      const lastScoreByTag = JSON.parse(lastScoreByTagJsonString);
      const scoringRubricResults = JSON.parse(scoringRubricResultsJsonString);
      const responseAnalysis = JSON.parse(responseAnalysisJsonString);
      const sessionTemplate = JSON.parse(sessionTemplateJsonString);
      const scoringSessionTemplate = JSON.parse(
        scoringSessionTemplateJsonString,
      );

      // const syncProps = syncDatasets({ lastScoreByItem, questionTagsById, ...ownProps });

      return {
        providerError: false,
        assignments: assignmentMetadata,
        responseAnalysisSignedRequest,
        scoringRubricRequest,
        lastScoreByItem,
        lastScoreByTag,
        questions,
        questionTagsById,
        scoringRubricResults,
        responseAnalysis,
        sessionTemplate,
        scoringSessionTemplate,
        sectionName,
        assessmentName,
        studentId,
        ...ownProps,
      };
    }
    return data;
  },
  error: ({ data: { error } }) => {
    log('error', error);
    return {
      providerError: true,
    };
  },
});

export default withAggregateResults;
